import { createContext, useState, useEffect, useRef, ReactNode, useContext } from 'react';
import { GeodataNode, ID, SimulationNode, SubSimulation } from 'types/project';
import { CurrentProjectContext } from './CurrentProjectContext';
import { updateGeodataVisibility } from '../api/geodataApi';
import { getProgress } from '../api/progressApi';
import { updateSimulationVisibility } from '../api/simulationApi';
import { runSimulationSet, type SimulationSetRunVersion } from '../api/simulationSetApi';
import { SIMULATION_PROGRESS_POLL_INTERVAL_MS } from 'constants/simulation';

type RunContextValue = {
  geodataStatuses: Record<ID, string>;
  allGeodatas: Record<ID, GeodataNode>;
  gridIdOpenButtonMap: Record<ID, boolean>;
  isRunDataLoading: boolean;
  removeGeodata: (geodataId: ID) => void;
  updateGeodataActive: (geodataId: ID, newValue: boolean) => Promise<void>;
  allSubSimulations: Record<ID, SubSimulation[]>;
  setAllSimulations: React.Dispatch<React.SetStateAction<Record<ID, SubSimulation[]>>>;
  geodataTaskStatuses: Record<ID, any>;
  handleRunSimulation: (simulation: SimulationNode, version: SimulationSetRunVersion) => Promise<void>;
  removeSimulation: (simulationId: ID) => void;
  isSubSimulationActive: (
    subSimulationStatuses: Record<ID, SubSimulation[]>,
    parentSimulationId: ID,
    subSimulationId: ID
  ) => boolean | string;
  getSubSimulationStatus: (
    subSimulationStatuses: Record<ID, SubSimulation[]>,
    parentSimulationId: ID,
    subSimulationId: ID
  ) => string;
  getSubSimulationMetrics: (
    subSimulationStatuses: Record<ID, SubSimulation[]>,
    parentSimulationId: ID,
    subSimulationId: ID
  ) => any;
  updateSubSimulationActive: (simulationId: ID, subSimId: ID, newValue: boolean) => Promise<void>;
};

export const RunContext = createContext<RunContextValue>({
  geodataStatuses: {},
  allGeodatas: {},
  gridIdOpenButtonMap: {},
  isRunDataLoading: false,
  removeGeodata: () => {},
  updateGeodataActive: async () => {},
  allSubSimulations: {},
  setAllSimulations: () => {},
  geodataTaskStatuses: {},
  handleRunSimulation: async () => {},
  removeSimulation: () => {},
  isSubSimulationActive: () => false,
  getSubSimulationStatus: () => 'No status info',
  getSubSimulationMetrics: () => 'No metrics info',
  updateSubSimulationActive: async () => {},
});

type RunContextProviderProps = {
  children?: ReactNode;
};

const logApiError = (err: unknown, message: string) => {
  console.error(message, err);
};

const RunContextProvider: React.FC<RunContextProviderProps> = ({ children }) => {
  const { currentProject } = useContext(CurrentProjectContext);
  const currentProjectId = currentProject?.id ?? null;
  const currentProjectIdRef = useRef<ID | null>(currentProjectId);
  useEffect(() => {
    currentProjectIdRef.current = currentProjectId;
  }, [currentProjectId]);

  const progressPollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isProgressPolling = useRef<boolean>(false);
  const isProgressRequestInFlight = useRef<boolean>(false);
  const geodataStatusesRef = useRef<Record<ID, string>>({});
  const allSubSimulationsRef = useRef<Record<ID, SubSimulation[]>>({});
  const allGeodatasRef = useRef<Record<ID, GeodataNode>>({});
  const pendingGeodataIdsRef = useRef<Set<ID>>(new Set());
  const pendingSimulationSetIdsRef = useRef<Set<ID>>(new Set());
  const validGeodataIdsRef = useRef<Set<ID>>(new Set());
  const validSimulationSetIdsRef = useRef<Set<ID>>(new Set());
  const previousProjectIdRef = useRef<ID | null>(currentProjectId);
  const simulationStartInFlightRef = useRef<boolean>(false);

  const [gridIdOpenButtonMap, setGridIdOpenButtonMap] = useState<Record<ID, boolean>>({});
  const [geodataStatuses, setGeodataStatuses] = useState<Record<ID, string>>({});
  const [geodataTaskStatuses, setGeodataTaskStatuses] = useState<Record<ID, any>>({});
  const [allSubSimulations, setAllSubSimulations] = useState<Record<ID, SubSimulation[]>>({});
  const [allGeodatas, setAllGeodatas] = useState<Record<ID, GeodataNode>>({});
  const [simulationsGeodataMap, setSimulationGeodataMap] = useState<Record<ID, ID>>({});
  const [geodataLoading, setGeodataLoading] = useState<boolean>(false);
  const [simulationLoading, setSimulationLoading] = useState<boolean>(false);
  const [geodataReady, setGeodataReady] = useState<boolean>(false);
  const [simulationReady, setSimulationReady] = useState<boolean>(false);
  const [readyProjectId, setReadyProjectId] = useState<ID | null>(null);
  const isRunDataLoading = currentProjectId
    ? geodataLoading || simulationLoading || !geodataReady || !simulationReady || currentProjectId !== readyProjectId
    : false;

  const resetPendingTargets = () => {
    pendingGeodataIdsRef.current.clear();
    pendingSimulationSetIdsRef.current.clear();
  };

  const clearProgressPollTimer = () => {
    if (progressPollTimer.current) {
      clearTimeout(progressPollTimer.current);
    }
    progressPollTimer.current = null;
  };

  const stopProgressPolling = () => {
    clearProgressPollTimer();
    isProgressPolling.current = false;
    isProgressRequestInFlight.current = false;
  };

  useEffect(() => {
    return () => stopProgressPolling();
  }, []);

  const updateGridStatus = (gridId: ID, value: boolean) => {
    setGridIdOpenButtonMap((prev) => {
      const updatedGrids = { ...prev };
      updatedGrids[gridId] = value;
      return updatedGrids;
    });
  };

  useEffect(() => {
    geodataStatusesRef.current = geodataStatuses;
  }, [geodataStatuses]);

  useEffect(() => {
    allSubSimulationsRef.current = allSubSimulations;
  }, [allSubSimulations]);

  useEffect(() => {
    allGeodatasRef.current = allGeodatas;
  }, [allGeodatas]);

  useEffect(() => {
    const previousId = previousProjectIdRef.current;
    if (!currentProjectId) {
      resetPendingTargets();
      stopProgressPolling();
    } else if (previousId && String(previousId) !== String(currentProjectId)) {
      resetPendingTargets();
      stopProgressPolling();
    }
    previousProjectIdRef.current = currentProjectId;
  }, [currentProjectId]);

  const removeGeodata = (geodataId: ID) => {
    setAllGeodatas((prev) => {
      const updated = { ...prev };
      delete updated[geodataId];
      return updated;
    });
    setGeodataStatuses((prev) => {
      const updated = { ...prev };
      delete updated[geodataId];
      return updated;
    });
    setGeodataTaskStatuses((prev) => {
      const updated = { ...prev };
      delete updated[geodataId];
      return updated;
    });
    validGeodataIdsRef.current.delete(geodataId);
    pendingGeodataIdsRef.current.delete(geodataId);
  };

  const removeSimulation = (simulationId: ID) => {
    setAllSubSimulations((prev) => {
      const updated = { ...prev };
      delete updated[simulationId];
      return updated;
    });
    validSimulationSetIdsRef.current.delete(simulationId);
    pendingSimulationSetIdsRef.current.delete(simulationId);
  };
  useEffect(() => {
    if (!currentProjectId || !currentProject?.grids) {
      setGridIdOpenButtonMap({});
      return;
    }
    const map = currentProject.grids.reduce<Record<ID, boolean>>((acc, grid) => {
      // Only map explicit backend flags; allow undefined to fall back to geodata status on UI side.
      if (grid.openVisualizerBtn !== undefined) {
        acc[grid.id] = Boolean(grid.openVisualizerBtn);
      }
      return acc;
    }, {});
    setGridIdOpenButtonMap(map);
  }, [currentProject, currentProjectId]);

  const syncGeodataStatuses = () => {
    if (!currentProjectId || !currentProject?.grids) {
      setGeodataStatuses({});
      setAllGeodatas({});
      validGeodataIdsRef.current = new Set();
      setGeodataLoading(false);
      setGeodataReady(false);
      return;
    }
    setGeodataLoading(true);
    const geodataStatusesData: Record<ID, string> = {};
    const geodatas: Record<ID, GeodataNode> = {};
    const previousStatuses = geodataStatusesRef.current;
    const previousGeodatas = allGeodatasRef.current;
    const volatileStatuses = new Set(['INITIALIZING', 'RUNNING']);
    (currentProject.grids ?? []).forEach((grid) => {
      (grid.geodatas ?? []).forEach((geodata) => {
        const backendStatus = geodata.status ?? '';
        const previousStatus = previousStatuses[geodata.id];
        const status = previousStatus && volatileStatuses.has(previousStatus) ? previousStatus : backendStatus;
        geodataStatusesData[geodata.id] = status;
        geodatas[geodata.id] = {
          ...(previousGeodatas[geodata.id] ?? {}),
          ...geodata,
          status,
        };
      });
    });
    setGeodataStatuses(geodataStatusesData);
    setAllGeodatas(geodatas);
    validGeodataIdsRef.current = new Set(Object.keys(geodataStatusesData) as ID[]);
    setGeodataLoading(false);
    setGeodataReady(true);
    setReadyProjectId(currentProjectId);
  };

  useEffect(() => {
    syncGeodataStatuses();
  }, [currentProject, currentProjectId]);

  useEffect(() => {
    if (!currentProjectId) {
      resetPendingTargets();
      setGeodataStatuses({});
      setGeodataTaskStatuses({});
      setAllSubSimulations({});
      setAllGeodatas({});
      validGeodataIdsRef.current = new Set();
      validSimulationSetIdsRef.current = new Set();
      setSimulationGeodataMap({});
      stopProgressPolling();
      setGeodataLoading(false);
      setSimulationLoading(false);
      setGeodataReady(false);
      setSimulationReady(false);
      setReadyProjectId(null);
      return;
    }
    // new project selected: reset readiness and trigger loaders immediately
    setGeodataReady(false);
    setSimulationReady(false);
    setGeodataLoading(true);
    setSimulationLoading(true);
    stopProgressPolling();
    setReadyProjectId(null);
  }, [currentProjectId]);

  // fetchSimulationStatuses
  useEffect(() => {
    const fetchSubSimulationStatuses = async () => {
      if (!currentProjectId) {
        setAllSubSimulations({});
        setSimulationGeodataMap({});
        validSimulationSetIdsRef.current = new Set();
        setSimulationLoading(false);
        setSimulationReady(false);
        return;
      }
      setSimulationLoading(true);
      try {
        const subSimulationStatusesData: Record<ID, SubSimulation[]> = {};
        const simulationsGeodataMap: Record<ID, ID> = {};
        const simulations: SimulationNode[] =
          (currentProject?.grids ?? []).flatMap((grid) =>
            (grid.geodatas ?? []).flatMap((geodata) =>
              (geodata.simulations ?? []).map((sim) => ({ ...sim, geodataId: geodata.id }))
            )
          ) ?? [];

        for (const simulation of simulations) {
          const subSimulations =
            simulation.subSimulations?.map((subSim) => {
              if (pendingSimulationSetIdsRef.current.has(simulation.id) && !subSim.status) {
                return { ...subSim, status: 'INITIALIZING' };
              }
              return subSim;
            }) ?? [];
          subSimulationStatusesData[simulation.id] = subSimulations;
          simulationsGeodataMap[simulation.id] = simulation.geodataId;
        }
        setAllSubSimulations(subSimulationStatusesData);
        setSimulationGeodataMap(simulationsGeodataMap);
        validSimulationSetIdsRef.current = new Set(Object.keys(subSimulationStatusesData) as ID[]);
      } catch (err) {
        logApiError(err, 'Error loading simulation statuses');
      } finally {
        setSimulationLoading(false);
        setSimulationReady(true);
        if (currentProjectId) {
          setReadyProjectId(currentProjectId);
        }
      }
    };

    fetchSubSimulationStatuses();
  }, [currentProject, currentProjectId]);

  // Fallback to avoid hanging loaders if readiness flags drift
  useEffect(() => {
    if (!currentProjectId) {
      return;
    }
    if (!geodataReady && !geodataLoading) {
      setGeodataReady(true);
    }
    if (!simulationReady && !simulationLoading) {
      setSimulationReady(true);
    }
    if (geodataReady && simulationReady && readyProjectId !== currentProjectId) {
      setReadyProjectId(currentProjectId);
    }
  }, [currentProjectId, geodataReady, simulationReady, geodataLoading, simulationLoading, readyProjectId]);

  // Force loading flags down once project exists to prevent stuck loaders
  useEffect(() => {
    if (currentProject && geodataLoading) {
      setGeodataLoading(false);
    }
    if (currentProject && simulationLoading) {
      setSimulationLoading(false);
    }
  }, [currentProject, geodataLoading, simulationLoading]);

  const updateSubSimulationActive = async (simulationId: ID, subSimId: ID, newValue: boolean) => {
    try {
      await updateSimulationVisibility(String(simulationId), subSimId, newValue);
      setAllSubSimulations((prev) => {
        const updatedSimulations = { ...prev };
        const targetSubSims = updatedSimulations[simulationId] || [];
        const updatedSubSims = targetSubSims.map((subSim) =>
          subSim.id === subSimId ? { ...subSim, isActive: newValue } : subSim
        );
        updatedSimulations[simulationId] = updatedSubSims;
        return updatedSimulations;
      });

      // After updating the sub simulation, check if we need to activate the related geodata:
      if (newValue === true && simulationsGeodataMap[simulationId]) {
        const geodataId = simulationsGeodataMap[simulationId];
        // Only activate if it's not already active:
        if (allGeodatas[geodataId] && !allGeodatas[geodataId].isActive) {
          await updateGeodataActive(geodataId, true);
        }
      }
    } catch (error) {
      logApiError(error, 'Failed to update subSimulation active state');
    }
  };
  const updateGeodataActive = async (geodataId: ID, newValue: boolean) => {
    const previousValue = allGeodatasRef.current[geodataId]?.isActive;
    setAllGeodatas((prev) => {
      const updatedGeodatas = { ...prev };
      updatedGeodatas[geodataId] = { ...updatedGeodatas[geodataId], isActive: newValue };

      return updatedGeodatas;
    });

    try {
      await updateGeodataVisibility(String(geodataId), newValue);
    } catch (error) {
      setAllGeodatas((prev) => {
        const updatedGeodatas = { ...prev };
        updatedGeodatas[geodataId] = { ...updatedGeodatas[geodataId], isActive: previousValue };

        return updatedGeodatas;
      });
      logApiError(error, 'Failed to update subSimulation active state');
    }
  };

  const RUNNING_STATUSES = ['INITIALIZING', 'RUNNING'];
  const TERMINAL_STATUSES = ['SUCCEEDED', 'FAILED', 'TIMED_OUT', 'ABORTED'];

  const collectProgressTargets = () => {
    const validGeodataIds = validGeodataIdsRef.current;
    const validSimulationSetIds = validSimulationSetIdsRef.current;

    const geodataIds = Object.entries(geodataStatusesRef.current || {})
      .filter(([, status]) => RUNNING_STATUSES.includes(status))
      .map(([id]) => id)
      .filter((id) => validGeodataIds.has(id as ID));

    const simulationSetIds = Object.entries(allSubSimulationsRef.current || {})
      .filter(([, subSims]) =>
        (subSims ?? []).some((subSim) => subSim?.status && RUNNING_STATUSES.includes(subSim.status))
      )
      .map(([id]) => id)
      .filter((id) => validSimulationSetIds.has(id as ID));

    pendingGeodataIdsRef.current.forEach((id) => {
      if (validGeodataIds.has(id) && !geodataIds.includes(id as string)) {
        geodataIds.push(id as string);
      }
    });
    pendingSimulationSetIdsRef.current.forEach((id) => {
      if (validSimulationSetIds.has(id) && !simulationSetIds.includes(id as string)) {
        simulationSetIds.push(id as string);
      }
    });

    return { geodataIds, simulationSetIds };
  };

  const mergeSubSimulationProgress = (existing: SubSimulation[], updates: Record<string, any>) => {
    const updatedSubSims = [...existing];

    Object.entries(updates ?? {}).forEach(([subId, payload]) => {
      const idx = updatedSubSims.findIndex((subSim) => String(subSim.id) === String(subId));
      const base = idx >= 0 ? updatedSubSims[idx] : ({ id: subId } as SubSimulation);
      const phase2 = (payload as any)?.phase2 ?? {};
      const status = typeof (payload as any)?.status === 'string' ? (payload as any).status : base.status;
      const updated: SubSimulation = {
        ...base,
        status,
        statusMessage: (payload as any)?.statusMessage ?? (payload as any)?.status_message ?? base.statusMessage,
        runId: (payload as any)?.runId ?? base.runId,
      };

      const percentage = (payload as any)?.percentage ?? phase2?.percentage;
      const remainingSeconds = (payload as any)?.remainingSeconds ?? phase2?.remainingSeconds;

      if (percentage !== undefined) {
        updated.percentage = percentage;
      }

      if (remainingSeconds !== undefined) {
        updated.expectedRemainingSeconds = remainingSeconds;
      }

      if ((payload as any)?.completed_merges !== undefined) {
        updated.completed_merges = (payload as any).completed_merges;
      }
      if ((payload as any)?.total_merges !== undefined) {
        updated.total_merges = (payload as any).total_merges;
      }
      if ((payload as any)?.exec_time !== undefined) {
        updated.execTime = (payload as any).exec_time;
      }
      if ((payload as any)?.execTime !== undefined) {
        updated.execTime = (payload as any).execTime;
      }
      if (status === 'SUCCEEDED' && base.status !== 'SUCCEEDED') {
        updated.isActive = true;
      }

      if (idx >= 0) {
        updatedSubSims[idx] = updated;
      } else {
        updatedSubSims.push(updated);
      }
    });

    return updatedSubSims;
  };

  const handleProgressResponse = async (data: any) => {
    const geodataResults = data?.geodatas ?? {};
    if (geodataResults && typeof geodataResults === 'object') {
      setGeodataTaskStatuses((prev) => {
        const updated = { ...prev };
        Object.entries(geodataResults).forEach(([geodataId, payload]) => {
          if ((payload as any)?.tasks) {
            updated[geodataId as ID] = (payload as any).tasks;
          }
        });
        return updated;
      });

      setGeodataStatuses((prev) => {
        const updated = { ...prev };
        Object.entries(geodataResults).forEach(([geodataId, payload]) => {
          const state =
            typeof (payload as any)?.state === 'string'
              ? (payload as any).state
              : (updated[geodataId as ID] ?? 'UNKNOWN');
          updated[geodataId as ID] = state;
        });
        return updated;
      });

      setAllGeodatas((prev) => {
        const updated = { ...prev };
        Object.entries(geodataResults).forEach(([geodataId, payload]) => {
          const state =
            typeof (payload as any)?.state === 'string' ? (payload as any).state : updated[geodataId as ID]?.status;
          updated[geodataId as ID] = {
            ...(updated[geodataId as ID] ?? {}),
            status: state,
            isActive: state === 'SUCCEEDED' && prev[geodataId].status !== 'SUCCEEDED' ? true : prev[geodataId].isActive,
            statusMessage:
              (payload as any)?.statusMessage ??
              (payload as any)?.status_message ??
              updated[geodataId as ID]?.statusMessage,
            execTime: (payload as any)?.execTime ?? (payload as any)?.exec_time ?? updated[geodataId as ID]?.execTime,
            runId: (payload as any)?.runId ?? updated[geodataId as ID]?.runId,
          };
        });
        return updated;
      });

      await Promise.all(
        Object.entries(geodataResults).map(async ([geodataId, payload]) => {
          const state = typeof (payload as any)?.state === 'string' ? (payload as any).state : null;
          const previousState = geodataStatusesRef.current[geodataId];

          if (state === 'SUCCEEDED' && previousState !== 'SUCCEEDED') {
            const gridId = allGeodatasRef.current[geodataId]?.gridId;
            if (gridId) {
              updateGridStatus(gridId, true);
            }
          }
          if (state && TERMINAL_STATUSES.includes(state)) {
            pendingGeodataIdsRef.current.delete(geodataId);
          } else if (state) {
            pendingGeodataIdsRef.current.add(geodataId);
          }
        })
      );
    }

    const simulationsResults = data?.simulations ?? {};
    if (simulationsResults && typeof simulationsResults === 'object') {
      setAllSubSimulations((prev) => {
        const updated = { ...prev };

        Object.entries(simulationsResults).forEach(([simulationId, payload]) => {
          const existing = updated[simulationId as ID] ?? [];
          const merged = mergeSubSimulationProgress(existing, payload as Record<string, any>);
          updated[simulationId as ID] = merged;

          const hasRunning = merged.some((subSim) => subSim.status && RUNNING_STATUSES.includes(subSim.status));
          const hasTerminal =
            merged.length > 0 && merged.every((subSim) => subSim.status && TERMINAL_STATUSES.includes(subSim.status));
          if (hasTerminal && !hasRunning) {
            pendingSimulationSetIdsRef.current.delete(simulationId);
          } else {
            pendingSimulationSetIdsRef.current.add(simulationId);
          }
        });

        return updated;
      });
    }
  };

  const pollProgress = async () => {
    const requestProjectId = currentProjectIdRef.current;
    if (!requestProjectId) {
      stopProgressPolling();
      return;
    }
    let cancelled = false;

    const { geodataIds, simulationSetIds } = collectProgressTargets();
    if (!geodataIds.length && !simulationSetIds.length) {
      stopProgressPolling();
      return;
    }

    isProgressPolling.current = true;
    if (isProgressRequestInFlight.current) {
      clearProgressPollTimer();
      progressPollTimer.current = setTimeout(pollProgress, SIMULATION_PROGRESS_POLL_INTERVAL_MS);
      return;
    }

    isProgressRequestInFlight.current = true;
    try {
      const data = await getProgress({
        geodataIds,
        simulationSetIds,
      });
      if (currentProjectIdRef.current !== requestProjectId) {
        cancelled = true;
        stopProgressPolling();
        return;
      }
      await handleProgressResponse(data);
    } catch (error) {
      logApiError(error, 'Error polling progress');
    } finally {
      isProgressRequestInFlight.current = false;
      clearProgressPollTimer();
      if (!cancelled) {
        progressPollTimer.current = setTimeout(pollProgress, SIMULATION_PROGRESS_POLL_INTERVAL_MS);
      }
    }
  };

  const ensureProgressPolling = () => {
    if (!isProgressPolling.current) {
      pollProgress();
    }
  };

  useEffect(() => {
    if (!currentProjectId) {
      stopProgressPolling();
      return;
    }

    const { geodataIds, simulationSetIds } = collectProgressTargets();

    if (
      (geodataIds.length > 0 || simulationSetIds.length > 0) &&
      !isProgressPolling.current &&
      !simulationStartInFlightRef.current
    ) {
      pollProgress();
    }

    if (geodataIds.length === 0 && simulationSetIds.length === 0 && isProgressPolling.current) {
      stopProgressPolling();
    }
  }, [currentProjectId, geodataStatuses, allSubSimulations]);

  const handleRunSimulation = async (pSimulation: SimulationNode, version: SimulationSetRunVersion) => {
    if (!currentProjectId) {
      return;
    }
    simulationStartInFlightRef.current = true;
    pendingSimulationSetIdsRef.current.add(pSimulation.id);
    // Mark subsimulations as starting so polling can kick in immediately (including onboarding flow).
    setAllSubSimulations((prev) => ({
      ...prev,
      [pSimulation.id]: (pSimulation.subSimulations ?? []).map((subSim) => ({
        ...subSim,
        status: 'INITIALIZING',
      })),
    }));

    try {
      await runSimulationSet(String(pSimulation.id), version);
      ensureProgressPolling();
    } catch (error) {
      logApiError(error, 'Error starting simulation');
    }
    simulationStartInFlightRef.current = false;
  };

  const isSubSimulationActive = (
    subSimulationStatuses: Record<ID, SubSimulation[]>,
    parentSimulationId: ID,
    subSimulationId: ID
  ) => {
    const index = Number(subSimulationId);
    if (subSimulationStatuses[parentSimulationId] && subSimulationStatuses[parentSimulationId].length > index) {
      return subSimulationStatuses[parentSimulationId][index].isActive ?? false;
    }
    return 'No status info';
  };

  const getSubSimulationStatus = (
    subSimulationStatuses: Record<ID, SubSimulation[]>,
    parentSimulationId: ID,
    subSimulationId: ID
  ) => {
    if (subSimulationStatuses[parentSimulationId]) {
      return (
        subSimulationStatuses[parentSimulationId].find((subSim) => subSim.id === subSimulationId)?.status ??
        'No status info'
      );
    }
    return 'No status info';
  };

  const getSubSimulationMetrics = (
    subSimulationStatuses: Record<ID, SubSimulation[]>,
    parentSimulationId: ID,
    subSimulationId: ID
  ) => {
    const index = Number(subSimulationId);
    if (subSimulationStatuses[parentSimulationId] && subSimulationStatuses[parentSimulationId].length > index) {
      return subSimulationStatuses[parentSimulationId][index].metrics;
    }
    return 'No metrics info';
  };
  return (
    <RunContext.Provider
      value={{
        geodataStatuses,
        allGeodatas,
        gridIdOpenButtonMap,
        isRunDataLoading,
        removeGeodata,
        updateGeodataActive,
        allSubSimulations,
        setAllSimulations: setAllSubSimulations,
        geodataTaskStatuses,
        handleRunSimulation,
        removeSimulation,
        isSubSimulationActive,
        getSubSimulationStatus,
        getSubSimulationMetrics,
        updateSubSimulationActive,
      }}
    >
      {children}
    </RunContext.Provider>
  );
};

export default RunContextProvider;
